import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import axios from "@/axios.config";
import { ApiResponse } from "@/types/fetch.type";
import {
  InternshipSiteDTO,
  InternshipSiteRequest,
  InternshipSiteUpdateRequest,
  InternshipSiteFilters,
  InternshipSitePaginationParams,
  InternshipSiteSearchParams,
  PagedInternshipSiteResponse,
  InternshipSiteExistsResponse,
  PaginationMeta,
} from "@/types/internship-sites.types";

// Query Keys
const INTERNSHIP_SITES_QUERY_KEY = "internship-sites";

// API Base URL
const BASE_URL = "/internship-sites";

/**
 * Get all internship sites with pagination and sorting
 */
export const useGetAllInternshipSites = (
  params?: InternshipSitePaginationParams
) => {
  return useQuery<
    ApiResponse<InternshipSiteDTO[]> & { meta?: { pagination: PaginationMeta } },
    AxiosError
  >({
    queryKey: [INTERNSHIP_SITES_QUERY_KEY, "all", params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined) {
        queryParams.append("page", params.page.toString());
      }
      if (params?.size !== undefined) {
        queryParams.append("size", params.size.toString());
      }
      if (params?.sortBy) {
        queryParams.append("sortBy", params.sortBy);
      }
      if (params?.direction) {
        queryParams.append("direction", params.direction);
      }

      const queryString = queryParams.toString();
      const url = `${BASE_URL}${queryString ? `?${queryString}` : ""}`;
      const response = await axios.get(url);
      return response.data;
    },
  });
};

/**
 * Get internship site by ID
 */
export const useGetInternshipSiteById = (id: string) => {
  return useQuery<ApiResponse<InternshipSiteDTO>, AxiosError>({
    queryKey: [INTERNSHIP_SITES_QUERY_KEY, "by-id", id],
    queryFn: async () => {
      const response = await axios.get(`${BASE_URL}/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

/**
 * Get all internship sites for a specific council (non-paginated list)
 */
export const useGetInternshipSitesByCouncil = (
  councilId: string,
  enabled: boolean = true
) => {
  return useQuery<ApiResponse<InternshipSiteDTO[]>, AxiosError>({
    queryKey: [INTERNSHIP_SITES_QUERY_KEY, "council", councilId],
    queryFn: async () => {
      const response = await axios.get(`${BASE_URL}/council/${councilId}`);
      return response.data;
    },
    enabled: enabled && !!councilId,
  });
};

/**
 * Get internship sites for a specific council with pagination
 */
export const useGetInternshipSitesByCouncilPaged = (
  councilId: string,
  params?: Omit<InternshipSitePaginationParams, "sortBy" | "direction">
) => {
  return useQuery<
    ApiResponse<InternshipSiteDTO[]> & { meta?: { pagination: PaginationMeta } },
    AxiosError
  >({
    queryKey: [INTERNSHIP_SITES_QUERY_KEY, "council-paged", councilId, params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined) {
        queryParams.append("page", params.page.toString());
      }
      if (params?.size !== undefined) {
        queryParams.append("size", params.size.toString());
      }

      const queryString = queryParams.toString();
      const url = `${BASE_URL}/council/${councilId}/paged${queryString ? `?${queryString}` : ""}`;
      const response = await axios.get(url);
      return response.data;
    },
    enabled: !!councilId,
  });
};

/**
 * Get internship sites by status (non-paginated list)
 */
export const useGetInternshipSitesByStatus = (
  status: string,
  enabled: boolean = true
) => {
  return useQuery<ApiResponse<InternshipSiteDTO[]>, AxiosError>({
    queryKey: [INTERNSHIP_SITES_QUERY_KEY, "status", status],
    queryFn: async () => {
      const response = await axios.get(`${BASE_URL}/status/${status}`);
      return response.data;
    },
    enabled: enabled && !!status,
  });
};

/**
 * Search internship sites with filters and pagination
 */
export const useSearchInternshipSites = (params?: InternshipSiteSearchParams) => {
  return useQuery<
    ApiResponse<InternshipSiteDTO[]> & { meta?: { pagination: PaginationMeta } },
    AxiosError
  >({
    queryKey: [INTERNSHIP_SITES_QUERY_KEY, "search", params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.title) {
        queryParams.append("title", params.title);
      }
      if (params?.status) {
        queryParams.append("status", params.status);
      }
      if (params?.councilId) {
        queryParams.append("councilId", params.councilId);
      }
      if (params?.page !== undefined) {
        queryParams.append("page", params.page.toString());
      }
      if (params?.size !== undefined) {
        queryParams.append("size", params.size.toString());
      }

      const queryString = queryParams.toString();
      const url = `${BASE_URL}/search${queryString ? `?${queryString}` : ""}`;
      const response = await axios.get(url);
      return response.data;
    },
  });
};

/**
 * Create internship site
 */
export const useCreateInternshipSite = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<InternshipSiteDTO>,
    AxiosError,
    InternshipSiteRequest
  >({
    mutationFn: async (data: InternshipSiteRequest) => {
      const response = await axios.post(BASE_URL, data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all internship sites queries
      queryClient.invalidateQueries({
        queryKey: [INTERNSHIP_SITES_QUERY_KEY],
      });
    },
  });
};

/**
 * Update internship site
 */
export const useUpdateInternshipSite = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<InternshipSiteDTO>,
    AxiosError,
    { id: string; data: InternshipSiteUpdateRequest }
  >({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`${BASE_URL}/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      // Invalidate all internship sites queries
      queryClient.invalidateQueries({
        queryKey: [INTERNSHIP_SITES_QUERY_KEY],
      });
      // Invalidate specific site query
      queryClient.invalidateQueries({
        queryKey: [INTERNSHIP_SITES_QUERY_KEY, "by-id", id],
      });
    },
  });
};

/**
 * Delete internship site (soft delete)
 */
export const useDeleteInternshipSite = () => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<null>, AxiosError, string>({
    mutationFn: async (id: string) => {
      const response = await axios.delete(`${BASE_URL}/${id}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all internship sites queries
      queryClient.invalidateQueries({
        queryKey: [INTERNSHIP_SITES_QUERY_KEY],
      });
    },
  });
};

/**
 * Check if internship site title exists for a council
 */
export const useCheckInternshipSiteExists = (
  title: string,
  councilId: string,
  enabled: boolean = true
) => {
  return useQuery<InternshipSiteExistsResponse, AxiosError>({
    queryKey: [INTERNSHIP_SITES_QUERY_KEY, "exists", title, councilId],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        title,
        councilId,
      });
      const response = await axios.get<InternshipSiteExistsResponse>(
        `${BASE_URL}/exists?${queryParams.toString()}`
      );
      return response.data;
    },
    enabled: enabled && !!title && !!councilId,
  });
};
